import styled, { css } from 'styled-components';
import { ICON_SIZES } from '@anwb/icon';
import { generateBoxShadow, pxToRem } from '@anwb/style-utilities';
import Typography from '@anwb/typography';
import { TOOLTIP_ICON_SIZE } from '../../components/brand-refresh/Tooltip';
import { ARROW_BOTTOM_OFFSET, ARROW_LEFT_OFFSET, ARROW_RIGHT_OFFSET, ARROW_TOP_OFFSET, OVERLAY_MAX_WIDTH, OVERLAY_MOBILE_GAP, TOOLTIP_GAP, } from '../../constants/brand-refresh';
/**
 * Tooltip Icon
 */
export const tooltipIconStyles = ({ theme, $inline, $variant, }) => css `
	display: inline-block;
	margin-left: ${pxToRem(theme.spacing['200'])};
	padding: 0 ${pxToRem(Number(theme.spacing['100']) + Number(theme.borderWidth['2']))} 0 0;
	width: auto;
	background-color: transparent;
	border: none;
	border-radius: 0;
	cursor: pointer;
	font-size: inherit; // this will make the button align with the text
	color: ${$variant === 'light'
    ? theme.colors.blanc.iconInteractiveInverse
    : theme.colors.highlight.iconTooltip};

	${$inline &&
    css `
		margin-right: ${pxToRem(theme.spacing['200'])};
	`}

	&:hover,
	&:focus {
		color: ${$variant === 'light'
    ? theme.colors.medium.iconInverseHover
    : theme.colors.highlight.iconTooltipHover};
	}
`;
export const TooltipIcon = styled.button `
	${tooltipIconStyles}
`;
/**
 * Tooltip Overlay Wrapper
 */
export const tooltipWrapperStyles = ({ theme }) => css `
	pointer-events: none;
	position: absolute;
	z-index: ${theme.zIndex.tooltip};
	width: calc(100% - ${pxToRem(OVERLAY_MOBILE_GAP)});
	height: ${pxToRem(theme.spacing['350'])};
	display: block;
	margin-left: ${pxToRem(OVERLAY_MOBILE_GAP / 2)};

	@media (min-width: ${pxToRem(theme.viewportBreakpoint.sm)}) {
		width: ${pxToRem(theme.spacing['350'])};
		margin: 0;
	}
`;
export const TooltipWrapper = styled.div `
	${tooltipWrapperStyles}
`;
/**
 * Tooltip Overlay Container
 */
const getTooltipOverlayContainerPosition = ($placement) => {
    switch ($placement) {
        case 'top':
            return css `
				top: -${pxToRem(TOOLTIP_GAP)};
				left: 50%;
				transform: translate(-50%, -100%);
				justify-content: center;
			`;
        case 'right':
            return css `
				top: 50%;
				right: -${pxToRem(TOOLTIP_GAP)};
				transform: translate(100%, -50%);
			`;
        case 'bottom':
            return css `
				bottom: -${pxToRem(TOOLTIP_GAP)};
				left: 50%;
				transform: translate(-50%, 100%);
				justify-content: center;
			`;
        case 'left':
            return css `
				top: 50%;
				left: -${pxToRem(TOOLTIP_GAP)};
				transform: translate(-100%, -50%);
				justify-content: flex-end;
			`;
        default:
            return css `
				top: -${pxToRem(TOOLTIP_GAP)};
				left: 50%;
				transform: translate(-50%, -100%);
				justify-content: center;
			`;
    }
};
export const tooltipOverlayContainerStyles = ({ theme, $placement, }) => css `
	position: absolute;
	width: calc(100% - ${pxToRem(OVERLAY_MOBILE_GAP * 2)});

	${getTooltipOverlayContainerPosition($placement)};

	@media (min-width: ${pxToRem(theme.viewportBreakpoint.sm)}) {
		width: ${pxToRem(OVERLAY_MAX_WIDTH)};
	}
`;
export const TooltipOverlayContainer = styled.div `
	${tooltipOverlayContainerStyles}
`;
/**
 * Tooltip Overlay Container Content
 */
export const tooltipOverlayContainerContentStyles = ({ theme, $variant, }) => css `
	pointer-events: all;
	padding: ${pxToRem(theme.spacing['200'])};

	border-radius: ${pxToRem(theme.borderRadius.s)};
	background-color: ${$variant === 'light'
    ? theme.colors.base.bgTooltipOnDark
    : theme.colors.base.bgTooltip};

	box-shadow: ${generateBoxShadow(theme.boxShadow.popover)};

	a {
		color: ${$variant === 'light'
    ? theme.colors.highlight.textLink
    : theme.colors.blanc.textLinkInverse};

		&:hover,
		&:focus {
			color: ${$variant === 'light'
    ? theme.colors.base.textBodyLinkHover
    : theme.colors.blanc.textLinkInverse};
		}

		&:active {
			color: ${$variant === 'light'
    ? theme.colors.highlight.textLink
    : theme.colors.blanc.textLinkInverse};
		}
	}

	img:last-child {
		margin-bottom: 0;
	}
`;
export const TooltipOverlayContainerContent = styled.div `
	${tooltipOverlayContainerContentStyles}
`;
/**
 * Tooltip Overlay Content
 */
export const tooltipOverlayContentStyles = ({ theme }) => css `
	display: inline-block;
	margin-right: ${pxToRem(theme.spacing['300'])};
`;
export const TooltipOverlayContent = styled.div `
	${tooltipOverlayContentStyles}
`;
/**
 * Tooltip Overlay Title
 */
export const tooltipOverlayTitleStyles = ({ theme, $variant, }) => css `
	color: ${$variant === 'light'
    ? theme.colors.base.textSubtitles
    : theme.colors.blanc.textTooltipOnDark};
	margin: 0;
	padding-bottom: ${pxToRem(theme.spacing['100'])};
`;
export const TooltipOverlayTitle = styled(Typography).attrs({
    variant: 'tooltip-title',
}) `
	${tooltipOverlayTitleStyles}
`;
/**
 * Tooltip Overlay Text
 */
export const tooltipOverlayTextStyles = ({ theme, $variant, }) => css `
	color: ${$variant === 'light'
    ? theme.colors.blanc.textTooltip
    : theme.colors.blanc.textTooltipOnDark};

	display: inline-block;

	* {
		color: currentColor;
	}
`;
export const TooltipOverlayText = styled(Typography).attrs({ variant: 'tooltip-text' }) `
	${tooltipOverlayTextStyles}
`;
/**
 * Tooltip Overlay Arrow Container
 */
export const tooltipOverlayArrowContainerStyles = () => css `
	pointer-events: none;
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	width: ${pxToRem(ICON_SIZES[TOOLTIP_ICON_SIZE])};
	height: ${pxToRem(ICON_SIZES[TOOLTIP_ICON_SIZE])};
`;
export const TooltipOverlayArrowContainer = styled.div `
	${tooltipOverlayArrowContainerStyles}
`;
/**
 * Tooltip Overlay Arrow
 */
const getArrowPosition = ($placement) => {
    switch ($placement) {
        case 'top':
            return css `
				transform: translate(0, -${pxToRem(TOOLTIP_GAP + ARROW_TOP_OFFSET)}) rotate(180deg);
			`;
        case 'right':
            return css `
				transform: translate(${pxToRem(TOOLTIP_GAP + ARROW_RIGHT_OFFSET)}, 0) rotateZ(-90deg);
			`;
        case 'bottom':
            return css `
				transform: translate(0, ${pxToRem(TOOLTIP_GAP + ARROW_BOTTOM_OFFSET)});
			`;
        case 'left':
            return css `
				transform: translate(-${pxToRem(TOOLTIP_GAP + ARROW_LEFT_OFFSET)}, 0) rotateZ(90deg);
			`;
        default:
            return css `
				transform: translate(0, ${pxToRem(TOOLTIP_GAP + ARROW_BOTTOM_OFFSET)});
			`;
    }
};
export const tooltipOverlayArrowStyles = ({ theme, $variant, $placement, $width, }) => css `
	${getArrowPosition($placement)}
	fill: ${$variant === 'light' ? theme.colors.base.bgTooltipOnDark : theme.colors.base.bgTooltip};
	z-index: ${theme.zIndex.tooltip};
	width: ${$width}px;

	${getArrowPosition($placement)}
`;
export const TooltipOverlayArrow = styled.svg `
	${tooltipOverlayArrowStyles}
`;
//# sourceMappingURL=_tooltip.styled.js.map